// Broker Channel Batch (WS6) — broker team dashboard at /brokers/teams/:id.
// Production rollups (deals sourced / loans originated by the team's channel-
// partner brokers), pipeline mix, and the member roster with add / remove.

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Plus } from "lucide-react";
import { brokersApi } from "@/api/brokers";
import type { BrokerStatus, BrokerSummaryDto } from "@/api/brokers";
import { brokerPickerLabel } from "@/lib/brokerDisplay";
import { brokerTeamsApi } from "@/api/brokerTeams";
import type { BrokerTeamMemberDto, BrokerTeamPipelineMixDto } from "@/api/brokerTeams";
import { extractErrorMessage } from "@/api/client";
import { brokerKeys, brokerTeamKeys } from "@/hooks/brokers/brokerKeys";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { MetricCard } from "@/components/portfolioAdmin/metrics/MetricsCards";
import {
  Badge,
  Button,
  Card,
  Input,
  Skeleton,
  StatusBadge,
  useToast,
} from "@/design-system/components/ui";
import { formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";

const STATUS_BADGE: Record<BrokerStatus, "gold" | "info" | "finalized" | "draft" | "review" | "archived" | "error"> = {
  Prospect: "info",
  Qualified: "gold",
  Onboarding: "review",
  Certified: "finalized",
  Inactive: "archived",
  Suspended: "review",
  Disqualified: "error",
  Declined: "archived",
};

const STAGE_LABEL: Record<string, string> = {
  Intake: "Intake",
  PreApproval: "Pre-approval",
  Underwriting: "Underwriting",
  Closing: "Closing",
  Closed: "Closed",
  Declined: "Declined",
  Withdrawn: "Withdrawn",
};

export function BrokerTeamDashboardPage() {
  const { tz } = useDateFormatter();
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const canUpdate = hasPermission("Brokers.Update");

  const [addOpen, setAddOpen] = useState(false);

  const dashboard = useQuery({
    queryKey: brokerTeamKeys.dashboard(id ?? ""),
    queryFn: () => brokerTeamsApi.dashboard(id!),
    enabled: !!id,
  });

  const remove = useMutation({
    mutationFn: (member: BrokerTeamMemberDto) =>
      brokerTeamsApi.removeMember(id!, member.brokerId),
    onSuccess: (_data, member) => {
      void queryClient.invalidateQueries({ queryKey: brokerTeamKeys.all });
      void queryClient.invalidateQueries({ queryKey: brokerKeys.all });
      toast.success(`${member.companyName} removed from the team.`);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  async function confirmRemove(member: BrokerTeamMemberDto) {
    const ok = await dialogs.confirm({
      title: "Remove broker?",
      message: `Remove ${member.companyName} from this team? The broker record itself is unaffected.`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    remove.mutate(member);
  }

  const memberColumns: Column<BrokerTeamMemberDto>[] = [
    {
      key: "company",
      header: "Broker",
      render: (m) => (
        <Link to={`/brokers/${m.brokerId}`} className="font-semibold hover:text-gold-shadow">
          {m.companyName}
        </Link>
      ),
    },
    {
      key: "name",
      header: "Contact",
      render: (m) => m.name ?? <span className="text-gray-mid">—</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (m) => <Badge status={STATUS_BADGE[m.status]}>{m.status}</Badge>,
    },
    {
      key: "graduated",
      header: "Graduated",
      render: (m) => (
        <span className="text-small text-gray-mid tabular-nums">
          {m.graduatedAt ? formatDateInTz(m.graduatedAt, tz) : "—"}
        </span>
      ),
    },
    {
      key: "deals",
      header: "Deals",
      className: "text-right tabular-nums",
      render: (m) => m.dealCount,
    },
    ...(canUpdate
      ? [
          {
            key: "actions",
            header: "",
            className: "w-10 text-right",
            render: (m: BrokerTeamMemberDto) => (
              <button
                type="button"
                className="text-caption text-status-error font-display uppercase tracking-wide hover:underline"
                onClick={() => void confirmRemove(m)}
                data-testid={`team-member-remove-${m.brokerId}`}
              >
                Remove
              </button>
            ),
          } satisfies Column<BrokerTeamMemberDto>,
        ]
      : []),
  ];

  if (!id) return null;

  if (dashboard.isLoading) {
    return (
      <div className="flex flex-col gap-space-3" data-testid="team-dashboard-loading">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (dashboard.isError || !dashboard.data) {
    return (
      <Card data-testid="team-dashboard-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Couldn't load this team.
        </p>
        <Button size="sm" variant="secondary" onClick={() => void dashboard.refetch()}>
          Retry
        </Button>
      </Card>
    );
  }

  const d = dashboard.data;

  return (
    <div className="flex flex-col gap-space-4" data-testid="team-dashboard">
      <div>
        <Link
          to="/brokers/teams"
          className="inline-flex items-center gap-1 text-caption text-gray-mid hover:text-onyx no-underline"
          data-testid="team-back-link"
        >
          <ChevronLeft size={14} aria-hidden /> Broker teams
        </Link>
        <PageHeader
          eyebrow="Partners"
          title={
            <span className="inline-flex items-baseline gap-space-3 flex-wrap">
              {d.name}
              {d.status === "Archived" && <StatusBadge tone="neutral">Archived</StatusBadge>}
            </span>
          }
          description={
            <>
              {d.ownerDisplayName ? `Owner: ${d.ownerDisplayName}` : "No owner assigned"}
              {" · Created "}
              {formatDateInTz(d.createdAt, tz)}
              {d.description ? ` — ${d.description}` : ""}
            </>
          }
          className="mb-0"
        />
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-space-3"
        data-testid="team-stat-tiles"
      >
        <MetricCard
          label="Brokers on team"
          value={d.brokerCount}
          testId="team-tile-brokers"
        />
        <MetricCard
          label="Deals sourced"
          value={d.dealsSourcedCount}
          caption={`${formatCurrency(d.dealsSourcedVolume)} volume`}
          testId="team-tile-deals"
        />
        <MetricCard
          label="Loans originated"
          value={d.loansOriginatedCount}
          caption={`${formatCurrency(d.loansOriginatedTotal)} total`}
          testId="team-tile-loans"
        />
        <MetricCard
          label="Avg deals / broker"
          value={d.avgDealsPerBroker.toFixed(1)}
          testId="team-tile-avg"
        />
        <MetricCard
          label="Active vs closed"
          value={`${d.activeDealCount} / ${d.closedDealCount}`}
          caption="active / closed deals"
          testId="team-tile-active-closed"
        />
      </div>

      <Card data-testid="team-pipeline-mix">
        <h2 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
          Pipeline mix
        </h2>
        <PipelineMixBars mix={d.pipelineMix} />
      </Card>

      <div className="flex flex-col gap-space-2">
        <div className="flex items-center justify-between gap-space-3">
          <h2 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0">
            Roster
          </h2>
          {canUpdate && (
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="team-add-broker">
              <Plus size={14} strokeWidth={1.5} aria-hidden /> Add broker
            </Button>
          )}
        </div>
        <DataTable<BrokerTeamMemberDto>
          columns={memberColumns}
          rows={d.members}
          emptyMessage="No brokers on this team yet."
          rowKey={(m) => m.brokerId}
          data-testid="team-roster-table"
        />
      </div>

      {addOpen && (
        <AddBrokerModal
          teamId={id}
          teamName={d.name}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

/** Simple horizontal count bars per pipeline stage (mirrors the portfolio
 *  dashboard's CSS-only distribution rendering — no chart lib). */
function PipelineMixBars({ mix }: { mix: BrokerTeamPipelineMixDto[] }) {
  const max = Math.max(...mix.map((m) => m.count), 0);
  if (max === 0) {
    return (
      <p className="text-small text-gray-mid m-0" data-testid="team-pipeline-mix-empty">
        No deals sourced by this team yet.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-space-1">
      {mix.map((m) => (
        <div
          key={m.stage}
          className="grid grid-cols-[8rem_1fr_2.5rem] items-center gap-space-2"
          data-testid={`team-stage-${m.stage}`}
        >
          <span className="text-caption text-gray-mid uppercase tracking-wide truncate">
            {STAGE_LABEL[m.stage] ?? m.stage}
          </span>
          <span className="h-2 rounded-sm bg-white-warm overflow-hidden">
            <span
              className="block h-full bg-gold rounded-sm"
              style={{ width: `${(m.count / max) * 100}%` }}
              aria-hidden
            />
          </span>
          <span className="text-small font-semibold tabular-nums text-right">{m.count}</span>
        </div>
      ))}
    </div>
  );
}

/** Eligible = Certified and not already on a team. The 100-row Pipeline page
 *  + a client-side search keeps this simple (the certified pool is small). */
function AddBrokerModal({
  teamId,
  teamName,
  onClose,
}: {
  teamId: string;
  teamName: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  // Server-side search (company / DBA / contact / email) so a certified
  // broker past the first page stays findable. The term rides the query key
  // (factory prefix preserved for invalidation) so React Query refetches as
  // it changes; eligibility (certified + unassigned) is still filtered here.
  const term = search.trim();
  const eligibleBrokersKey = [...brokerTeamKeys.eligibleBrokers(), term];
  const brokers = useQuery({
    queryKey: eligibleBrokersKey,
    queryFn: () =>
      brokersApi.list({ page: 1, pageSize: 100, q: term || undefined, view: "Pipeline" }),
  });

  const eligible = useMemo(() => {
    return (brokers.data?.data ?? []).filter(
      (b) => b.status === "Certified" && !b.teamId,
    );
  }, [brokers.data]);

  const assign = useMutation({
    mutationFn: (broker: BrokerSummaryDto) => brokerTeamsApi.assignMember(teamId, broker.id),
    onSuccess: (_data, broker) => {
      void queryClient.invalidateQueries({ queryKey: brokerTeamKeys.all });
      void queryClient.invalidateQueries({ queryKey: brokerKeys.lists() });
      toast.success(`${broker.companyName} assigned to "${teamName}".`);
      onClose();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Add broker"
      description="Certified brokers not yet on a team are eligible."
      size="sm"
    >
      <div className="flex flex-col gap-space-3" data-testid="team-add-broker-modal">
        <Input
          type="search"
          placeholder="Search brokers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
        {brokers.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : eligible.length === 0 ? (
          <p className="text-small text-gray-mid m-0" data-testid="team-add-broker-empty">
            No eligible brokers found.
          </p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col divide-y divide-border-light max-h-80 overflow-y-auto">
            {eligible.map((b) => {
              const { primary, secondary } = brokerPickerLabel(b);
              return (
              <li key={b.id} className="flex items-center justify-between gap-space-2 py-space-2">
                <div className="min-w-0">
                  <div className="font-semibold text-small text-onyx truncate">{primary}</div>
                  <div className="text-caption text-gray-mid truncate">
                    {secondary || (b.state ?? "—")}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={assign.isPending}
                  onClick={() => assign.mutate(b)}
                  data-testid={`team-add-broker-${b.id}`}
                >
                  Add
                </Button>
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
